import React, { useState } from 'react';
import { Star, Send, CheckCircle } from 'lucide-react';
import { Button } from './ui/button';
import { Input } from './ui/input';
import { Textarea } from './ui/textarea';
import { Label } from './ui/label';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from './ui/select';
import axios from 'axios';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const ReviewForm = () => {
  const [formData, setFormData] = useState({
    name: '',
    location: '',
    rating: 5,
    text: '',
    service_type: '',
  });
  const [isSubmitted, setIsSubmitted] = useState(false);
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [hoveredRating, setHoveredRating] = useState(0);

  const handleChange = (e) => {
    setFormData({ ...formData, [e.target.name]: e.target.value });
  };

  const handleServiceChange = (value) => {
    setFormData({ ...formData, service_type: value });
  };

  const handleRatingClick = (rating) => {
    setFormData({ ...formData, rating });
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setIsSubmitting(true);
    
    try {
      await axios.post(`${API}/reviews`, formData);
      setIsSubmitted(true);
      setFormData({ name: '', location: '', rating: 5, text: '', service_type: '' });
    } catch (error) {
      console.error('Error submitting review:', error);
      alert('Failed to submit review. Please try again.');
    } finally {
      setIsSubmitting(false);
    }
  };

  if (isSubmitted) {
    return (
      <div className="bg-white border border-neutral-200 rounded-2xl p-8 text-center">
        <div className="w-16 h-16 bg-brand/20 rounded-full flex items-center justify-center mx-auto mb-4">
          <CheckCircle className="w-8 h-8 text-brand" />
        </div>
        <h3 className="text-xl font-bold text-neutral-900 mb-2">Thank You!</h3>
        <p className="text-neutral-600 mb-4">
          Your review has been submitted and is pending approval. We appreciate your feedback!
        </p>
        <Button
          onClick={() => setIsSubmitted(false)}
          variant="outline"
          className="border-brand text-brand hover:bg-brand/10"
        >
          Submit Another Review
        </Button>
      </div>
    );
  }

  return (
    <div className="bg-white border border-neutral-200 rounded-2xl p-8">
      <h3 className="text-xl font-bold text-neutral-900 mb-2">Leave a Review</h3>
      <p className="text-neutral-600 mb-6">Share your experience with Eliminex Pest Solutions</p>
      
      <form onSubmit={handleSubmit} className="space-y-5">
        {/* Rating */}
        <div className="space-y-2">
          <Label className="text-neutral-700">Your Rating *</Label>
          <div className="flex gap-2">
            {[1, 2, 3, 4, 5].map((star) => (
              <button
                key={star}
                type="button"
                onClick={() => handleRatingClick(star)}
                onMouseEnter={() => setHoveredRating(star)}
                onMouseLeave={() => setHoveredRating(0)}
                className="focus:outline-none"
              >
                <Star
                  className={`w-8 h-8 transition-colors ${
                    star <= (hoveredRating || formData.rating)
                      ? 'text-brand fill-brand'
                      : 'text-neutral-300'
                  }`}
                />
              </button>
            ))}
          </div>
        </div>

        <div className="grid sm:grid-cols-2 gap-4">
          <div className="space-y-2">
            <Label htmlFor="review-name" className="text-neutral-700">
              Your Name *
            </Label>
            <Input
              id="review-name"
              name="name"
              value={formData.name}
              onChange={handleChange}
              required
              placeholder="John S."
              className="bg-white border-neutral-300 text-neutral-900 placeholder:text-neutral-400 focus:border-brand"
            />
          </div>
          <div className="space-y-2">
            <Label htmlFor="review-location" className="text-neutral-700">
              Location *
            </Label>
            <Input
              id="review-location"
              name="location"
              value={formData.location}
              onChange={handleChange}
              required
              placeholder="Lincoln"
              className="bg-white border-neutral-300 text-neutral-900 placeholder:text-neutral-400 focus:border-brand"
            />
          </div>
        </div>

        <div className="space-y-2">
          <Label className="text-neutral-700">Service Received</Label>
          <Select onValueChange={handleServiceChange} value={formData.service_type}>
            <SelectTrigger className="bg-white border-neutral-300 text-neutral-900 focus:border-brand">
              <SelectValue placeholder="Select service (optional)" />
            </SelectTrigger>
            <SelectContent className="bg-white border-neutral-200">
              <SelectItem value="bed-bugs" className="text-neutral-900 hover:bg-brand/10">
                Bed Bug Control
              </SelectItem>
              <SelectItem value="rodents" className="text-neutral-900 hover:bg-brand/10">
                Rodent Control
              </SelectItem>
              <SelectItem value="insects" className="text-neutral-900 hover:bg-brand/10">
                Insect Control
              </SelectItem>
              <SelectItem value="wasps" className="text-neutral-900 hover:bg-brand/10">
                Wasp Treatment
              </SelectItem>
              <SelectItem value="birds" className="text-neutral-900 hover:bg-brand/10">
                Bird Control
              </SelectItem>
              <SelectItem value="commercial" className="text-neutral-900 hover:bg-brand/10">
                Commercial Services
              </SelectItem>
              <SelectItem value="other" className="text-neutral-900 hover:bg-brand/10">
                Other
              </SelectItem>
            </SelectContent>
          </Select>
        </div>

        <div className="space-y-2">
          <Label htmlFor="review-text" className="text-neutral-700">
            Your Review *
          </Label>
          <Textarea
            id="review-text"
            name="text"
            value={formData.text}
            onChange={handleChange}
            required
            rows={4}
            placeholder="Tell us about your experience..."
            className="bg-white border-neutral-300 text-neutral-900 placeholder:text-neutral-400 focus:border-brand resize-none"
          />
        </div>

        <Button
          type="submit"
          disabled={isSubmitting}
          className="w-full bg-brand hover:bg-brand-dark text-white font-bold py-5"
        >
          {isSubmitting ? (
            <span className="flex items-center justify-center gap-2">
              <div className="w-5 h-5 border-2 border-white/30 border-t-white rounded-full animate-spin" />
              Submitting...
            </span>
          ) : (
            <span className="flex items-center justify-center gap-2">
              <Send className="w-5 h-5" />
              Submit Review
            </span>
          )}
        </Button>
      </form>
    </div>
  );
};

export default ReviewForm;
